import { useState } from 'react';
import { ArrowRight, CheckCircle, Loader2, Zap, Bell, Gift, Users } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const JoinWaitlistPage = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // In a real app, this would send to a waitlist API
      await new Promise(resolve => setTimeout(resolve, 1500));
      setSubmitted(true);
      toast.success('You\'re on the list!');
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    {
      icon: <Bell size={24} strokeWidth={1.5} />,
      title: "Early Access",
      description: "Be the first to know about exclusive events and new features"
    },
    {
      icon: <Gift size={24} strokeWidth={1.5} />,
      title: "Founding Member Perks",
      description: "Get special discounts and priority access to sold-out events"
    },
    {
      icon: <Users size={24} strokeWidth={1.5} />,
      title: "Community Access",
      description: "Join our private community of underground music lovers"
    }
  ];

  const cities = [
    // UK Cities
    "London",
    "Manchester",
    "Birmingham",
    "Bristol",
    "Leeds",
    // India Cities
    "Mumbai",
    "Delhi",
    "Bangalore",
    "Hyderabad",
    "Chennai",
    "Pune",
    "Kolkata",
    "Goa",
    "Other"
  ];

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="waitlist-page">
      {/* Hero Section */}
      <section className="relative min-h-[40vh] flex items-center justify-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url('https://images.unsplash.com/photo-1514525253161-7a46d19cd819?q=80&w=1920&auto=format&fit=crop')`,
            }}
          />
          <div className="absolute inset-0 bg-black/85" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#050505]" />
        </div>

        {/* Content */}
        <div className="relative container mx-auto px-4 md:px-8 text-center pt-24">
          <div className="inline-flex items-center gap-2 px-4 py-2 border border-[#B5FF00]/30 mb-6 opacity-0 animate-fade-in" style={{ animationDelay: '0.2s', animationFillMode: 'forwards' }}>
            <Zap size={14} className="text-[#B5FF00]" />
            <span className="font-mono text-xs tracking-widest text-[#B5FF00]">BETA LAUNCHING SOON</span>
          </div>
          <h1 
            className="font-['Josefin_Sans'] text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-4 opacity-0 animate-fade-in-up"
            style={{ animationDelay: '0.3s', animationFillMode: 'forwards' }}
            data-testid="waitlist-title"
          >
            JOIN THE <span className="text-[#B5FF00]">WAITLIST</span>
          </h1>
          <p 
            className="text-zinc-400 text-lg max-w-xl mx-auto opacity-0 animate-fade-in-up"
            style={{ animationDelay: '0.4s', animationFillMode: 'forwards' }}
          >
            Get early access to the UK & India's premier underground nightlife platform
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Left: Benefits */}
            <div>
              <span className="font-mono text-xs tracking-widest text-[#B5FF00] mb-4 block">
                WHAT YOU GET
              </span>
              <h2 className="font-['Josefin_Sans'] text-3xl md:text-4xl font-bold text-white mb-8">
                FOUNDING MEMBER BENEFITS
              </h2>

              <div className="space-y-6">
                {benefits.map((benefit, index) => (
                  <div 
                    key={benefit.title}
                    className="flex gap-4 p-6 bg-[#0A0A0A] border border-zinc-800 hover:border-[#B5FF00]/30 transition-colors opacity-0 animate-fade-in-up"
                    style={{ animationDelay: `${index * 100}ms`, animationFillMode: 'forwards' }}
                  >
                    <div className="text-[#B5FF00] flex-shrink-0">
                      {benefit.icon}
                    </div>
                    <div>
                      <h3 className="font-['Josefin_Sans'] text-xl text-white mb-1">{benefit.title}</h3>
                      <p className="text-zinc-500 text-sm">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Stats */}
              <div className="mt-12 grid grid-cols-2 gap-6">
                <div className="text-center p-6 border border-zinc-800">
                  <div className="font-['Josefin_Sans'] text-4xl text-[#B5FF00] mb-1">2,500+</div>
                  <div className="font-mono text-xs text-zinc-500">ON WAITLIST</div>
                </div>
                <div className="text-center p-6 border border-zinc-800">
                  <div className="font-['Josefin_Sans'] text-4xl text-[#B5FF00] mb-1">9</div>
                  <div className="font-mono text-xs text-zinc-500">CITIES COVERED</div>
                </div>
              </div>
            </div>

            {/* Right: Form */}
            <div>
              {submitted ? (
                <div className="bg-[#0A0A0A] border border-[#B5FF00]/30 p-8 md:p-12 text-center animate-fade-in">
                  <div className="w-20 h-20 rounded-full bg-[#B5FF00]/10 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle size={40} className="text-[#B5FF00]" />
                  </div>
                  <h3 className="font-['Josefin_Sans'] text-3xl text-white mb-4">YOU'RE ON THE LIST!</h3>
                  <p className="text-zinc-400 mb-6">
                    Thanks for joining the waitlist. We'll notify you when we launch in your city.
                  </p>
                  <p className="font-mono text-sm text-zinc-600">
                    Check your inbox for a confirmation email.
                  </p>
                </div>
              ) : (
                <div className="bg-[#0A0A0A] border border-zinc-800 p-8 md:p-12">
                  <h3 className="font-['Josefin_Sans'] text-2xl text-white mb-6">GET EARLY ACCESS</h3>
                  
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="waitlist-form">
                    <div>
                      <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">
                        NAME
                      </label>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="w-full h-12 bg-[#050505] border border-zinc-800 text-white px-4 focus:border-[#B5FF00] focus:outline-none transition-colors"
                        placeholder="Your name"
                        required
                        data-testid="waitlist-name"
                      />
                    </div>

                    <div>
                      <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">
                        EMAIL
                      </label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full h-12 bg-[#050505] border border-zinc-800 text-white px-4 focus:border-[#B5FF00] focus:outline-none transition-colors"
                        placeholder="your@email.com"
                        required
                        data-testid="waitlist-email"
                      />
                    </div>

                    <div>
                      <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">
                        CITY
                      </label>
                      <select
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        className="w-full h-12 bg-[#050505] border border-zinc-800 text-white px-4 focus:border-[#B5FF00] focus:outline-none transition-colors appearance-none"
                        required
                        data-testid="waitlist-city"
                      >
                        <option value="">Select your city</option>
                        {cities.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="btn-primary w-full flex items-center justify-center gap-2 animate-pulse-glow"
                      data-testid="waitlist-submit"
                    >
                      {loading ? (
                        <>
                          <Loader2 size={18} className="animate-spin" />
                          JOINING...
                        </>
                      ) : (
                        <>
                          JOIN WAITLIST
                          <ArrowRight size={18} />
                        </>
                      )}
                    </button>

                    <p className="text-zinc-600 text-xs text-center font-mono">
                      No spam, ever. Unsubscribe anytime.
                    </p>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <div className="border-t border-b border-zinc-800 py-4 overflow-hidden">
        <div className="marquee">
          <div className="marquee-content">
            <span className="text-zinc-700 font-mono text-sm tracking-widest whitespace-nowrap">
              &nbsp;&nbsp;⚡ COMMUNITY + PROMOTION + TICKETING ⚡ JOIN THE BETA ⚡ COMMUNITY + PROMOTION + TICKETING ⚡ JOIN THE BETA&nbsp;&nbsp;
            </span>
            <span className="text-zinc-700 font-mono text-sm tracking-widest whitespace-nowrap">
              &nbsp;&nbsp;⚡ COMMUNITY + PROMOTION + TICKETING ⚡ JOIN THE BETA ⚡ COMMUNITY + PROMOTION + TICKETING ⚡ JOIN THE BETA&nbsp;&nbsp;
            </span>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-8 border-t border-zinc-800 relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
          <span className="footer-brand">UNIGHT</span>
        </div>
        
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_venue-pulse-14/artifacts/8f7het1z_Unight%202d%20Logos%20%28TM%29-05.png"
              alt="Unight"
              className="h-8"
            />
            <span className="font-mono text-xs text-zinc-600 tracking-widest">
              © 2025 UNIGHT. ALL RIGHTS RESERVED.
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default JoinWaitlistPage;
